import { useState, useEffect } from 'react';
import { featureFlags as defaultFeatureFlags, type FeatureFlags } from '@/config/feature-flags';

const STORAGE_KEY = 'stock-feature-flags';

/**
 * Hook to manage feature flags with localStorage persistence
 * 
 * This allows feature flags to be toggled in the UI and persisted across sessions.
 * If no flags are stored, it uses the defaults from feature-flags.ts
 */
export function useFeatureFlags() {
  const [flags, setFlags] = useState<FeatureFlags>(() => {
    // Load from localStorage on initial mount
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          return { ...defaultFeatureFlags, ...JSON.parse(stored) };
        } catch (e) {
          console.error('Failed to parse stored feature flags:', e);
        }
      }
    }
    return defaultFeatureFlags;
  });

  // Save to localStorage whenever flags change
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(flags));
    }
  }, [flags]);

  const updateFlag = (key: keyof FeatureFlags, value: boolean) => {
    setFlags(prev => ({ ...prev, [key]: value }));
  };

  const resetToDefaults = () => {
    setFlags(defaultFeatureFlags);
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  const enableAll = () => {
    const allEnabled = Object.keys(flags).reduce((acc, key) => {
      acc[key as keyof FeatureFlags] = true;
      return acc;
    }, {} as FeatureFlags);
    setFlags(allEnabled);
  };

  const disableAll = () => {
    const allDisabled = Object.keys(flags).reduce((acc, key) => {
      acc[key as keyof FeatureFlags] = false;
      return acc;
    }, {} as FeatureFlags);
    setFlags(allDisabled);
  };

  return {
    flags,
    updateFlag,
    resetToDefaults,
    enableAll,
    disableAll,
  };
}

/**
 * Hook to check if a specific feature is enabled
 * 
 * @param featureName - The name of the feature flag to check
 * @returns boolean indicating if the feature is enabled
 */
export function useFeatureFlag(featureName: keyof FeatureFlags): boolean {
  const { flags } = useFeatureFlags();
  return flags[featureName];
}
